import React from 'react';
import { BiProvider } from '@wix/cli-telemetry-react';
import { render } from '@wix/cli-ui-kit';
import { LoginModeProvider } from '@wix/cli-auth';
import type { LoginMode } from '@wix/cli-core-definitions';
import type { UserInfo } from '@wix/cli-auth';
import { I18nProvider } from '@wix/cli-i18n';
import { Command, Option } from '@commander-js/extra-typings';
import { ErrorReporterProvider } from '@wix/cli-error-reporting';
import type { ErrorReporter } from '@wix/cli-error-reporting';
import {
  HttpClientProvider,
  buildHttpClientHeaders,
} from '@wix/cli-http-client-react';
import type { RepoType, IPackageManager } from '@wix/package-manager';
import type { Panorama } from '@wix/cli-panorama';
import { createCommandTransaction, PanoramaProvider } from '@wix/cli-panorama';
import { createBiLogger } from './bi/index.js';
import { CreateAppCommand } from './components/CreateAppCommand.js';
import {
  validateUUID,
  validateGitUrl,
  validateTemplateParams,
  validateAppName,
} from './validations/index.js';
import messages from './messages.json' with { type: 'json' };

export const getAppCommand = ({
  errorReporter,
  userInfo,
  loginMode,
  panorama,
  cliVersion,
  cliSessionId,
  targetParentFolder,
  packageManager,
  repoType,
  aiAgentName,
}: {
  errorReporter: ErrorReporter;
  userInfo: UserInfo | null;
  loginMode?: LoginMode;
  panorama: Panorama;
  cliVersion: string;
  cliSessionId: string;
  targetParentFolder: string;
  packageManager: IPackageManager;
  repoType: RepoType;
  aiAgentName?: string;
}) => {
  return new Command()
    .name('app')
    .description('Create Wix app')
    .addOption(
      new Option(
        '--skip-install',
        'Skip automatic dependency installation.'
      ).hideHelp()
    )
    .addOption(
      new Option(
        '--skip-git',
        'Prevent the app from automatically initializing a Git repository during setup.'
      ).hideHelp()
    )
    .addOption(
      new Option(
        '-r, --template-repo <template-repo>',
        'Git repository URL to fetch the template from.'
      )
        .argParser(validateGitUrl)
        .hideHelp()
    )
    .addOption(
      new Option(
        '-p, --template-repo-path <template-repo-path>',
        'Path inside the Git repository to fetch the template from.'
      ).hideHelp()
    )
    .option(
      '-t, --template <template-id>',
      'Template ID to use for the app creation.',
      validateUUID
    )
    .addOption(
      new Option(
        '-x, --template-path <template-path>',
        'Local path to the template folder.'
      ).hideHelp()
    )
    .addOption(
      new Option(
        '--template-params <template-params>',
        'Additional template parameters to be provided to the app template should be formatted as a JSON string. These parameters can be accessed through the templateParams variable.'
      )
        .argParser(validateTemplateParams)
        .hideHelp()
    )
    .addOption(
      new Option(
        '--app-name <app-name>',
        'The name for your new app.'
      ).argParser(validateAppName)
    )
    .addOption(
      new Option(
        '--extend-app-id <extend-app-id>',
        'The ID of the app you want to extend'
      )
        .argParser(validateUUID)
        .conflicts('template')
    )
    .action(async (options, command) => {
      errorReporter.setContext('Command Info', {
        Arguments: command.args,
        Options: command.opts(),
      });

      const { biLogger, reportCommandStartEvent } = await createBiLogger({
        errorReporter,
        cliVersion,
        cliSessionId,
        userId: userInfo?.userId,
        aiAgentName,
      });

      const commandStatus = reportCommandStartEvent(command);

      const commandOptions = {
        ...options,
        skipInstall: options.skipInstall ?? false,
        skipGit: options.skipGit ?? false,
      };

      const headers = buildHttpClientHeaders({
        flow: 'CreateApp',
        command: command.name(),
        cliVersion,
      });

      const transaction = createCommandTransaction(panorama, command);

      try {
        transaction.start();

        await render(
          <BiProvider value={biLogger}>
            <ErrorReporterProvider value={errorReporter}>
              <PanoramaProvider value={panorama}>
                <HttpClientProvider headers={headers}>
                  <I18nProvider messages={messages}>
                    <LoginModeProvider value={loginMode}>
                      <CreateAppCommand
                        userInfo={userInfo}
                        targetParentFolder={targetParentFolder}
                        commandOptions={commandOptions}
                        packageManager={packageManager}
                        repoType={repoType}
                      />
                    </LoginModeProvider>
                  </I18nProvider>
                </HttpClientProvider>
              </PanoramaProvider>
            </ErrorReporterProvider>
          </BiProvider>
        );

        transaction.finish();
        commandStatus.succeed();
      } catch (e) {
        commandStatus.failed(e);
        throw e;
      }
    });
};
